import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ImagePlus, Send, MessageCircle, ArrowLeft } from "lucide-react";

const POLL_MS = 7000;

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Messages() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { otherUserId } = useParams();
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState({ other: null, messages: [] });
  const [body, setBody] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  const loadThreads = useCallback(() => {
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  const loadThread = useCallback(() => {
    if (!otherUserId) return;
    api.get(`/messages/thread/${otherUserId}`)
      .then((r) => setThread(r.data))
      .catch(() => setThread({ other: null, messages: [] }));
  }, [otherUserId]);

  useEffect(() => {
    if (!user) return;
    loadThreads();
    const id = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(id);
  }, [user, loadThreads]);

  useEffect(() => {
    if (!user || !otherUserId) return;
    loadThread();
    const id = setInterval(loadThread, POLL_MS);
    return () => clearInterval(id);
  }, [user, otherUserId, loadThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.messages]);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("Only images can be attached.");
      return;
    }
    try {
      const url = await fileToDataURL(f);
      setImageDataUrl(url);
    } catch {
      toast.error("Could not read that image.");
    } finally {
      e.target.value = "";
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!otherUserId) return;
    if (!body.trim() && !imageDataUrl) {
      toast.error("Write something or attach an image.");
      return;
    }
    setSending(true);
    try {
      await api.post("/messages", {
        to_user_id: otherUserId,
        body: body.trim() || null,
        image_data_url: imageDataUrl,
      });
      setBody("");
      setImageDataUrl(null);
      loadThread();
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10" data-testid="messages-page">
      <div className="label text-pfc-gold mb-2">PFC · Direct Messages</div>
      <h1 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
        Your <span className="gold-text">Inbox</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 min-h-[600px]">
        {/* THREAD LIST */}
        <aside className={`pfc-card overflow-hidden ${otherUserId ? "hidden lg:block" : ""}`} data-testid="threads-list">
          <div className="px-5 py-4 border-b border-white/10">
            <p className="font-sub uppercase tracking-[0.2em] text-xs text-white/60">Conversations</p>
          </div>
          {threads.length === 0 ? (
            <div className="p-6 text-white/40 font-body text-sm" data-testid="threads-empty">
              No conversations yet. Visit the <Link to="/community" className="text-pfc-gold hover:underline">Community</Link> and message someone.
            </div>
          ) : (
            <ul className="divide-y divide-white/5 max-h-[640px] overflow-y-auto">
              {threads.map((t) => (
                <li key={t.other?.user_id}>
                  <Link
                    to={`/messages/${t.other?.user_id}`}
                    className={`flex items-start gap-3 px-5 py-4 hover:bg-white/5 transition ${otherUserId === t.other?.user_id ? "bg-pfc-gold/10" : ""}`}
                    data-testid={`thread-link-${t.other?.user_id}`}
                  >
                    <div className="w-10 h-10 bg-pfc-gold/20 border border-pfc-gold/40 flex items-center justify-center font-display text-pfc-gold shrink-0" style={{ borderRadius: 5 }}>
                      {(t.other?.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-display text-white text-base truncate">{t.other?.name || "Member"}</p>
                        {t.unread_count > 0 && (
                          <span className="bg-pfc-red text-white text-[10px] font-sub uppercase tracking-wider px-2 py-0.5 rounded-full">{t.unread_count}</span>
                        )}
                      </div>
                      <p className="text-white/55 text-xs font-body truncate mt-0.5">
                        {t.last_message?.body || (t.last_message?.image_data_url ? "📷 Image" : "—")}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* THREAD */}
        <section className={`pfc-card flex flex-col ${!otherUserId ? "hidden lg:flex" : ""}`} data-testid="thread-view">
          {!otherUserId ? (
            <div className="flex-1 flex items-center justify-center text-white/40 font-sub uppercase tracking-[0.2em] p-10">
              <div className="text-center">
                <MessageCircle size={36} className="mx-auto mb-4 text-pfc-gold/40" />
                <p>Select a conversation</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                <Link to="/messages" className="lg:hidden text-white/60 hover:text-white">
                  <ArrowLeft size={18} />
                </Link>
                <div className="w-9 h-9 bg-pfc-gold/20 border border-pfc-gold/40 flex items-center justify-center font-display text-pfc-gold" style={{ borderRadius: 5 }}>
                  {(thread.other?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <p className="font-display text-white text-base" data-testid="thread-other-name">{thread.other?.name || "—"}</p>
                  <p className="text-white/50 text-xs font-body">{thread.other?.headline}</p>
                </div>
                {thread.other?.profile_id && (
                  <Link
                    to={`/profile/${thread.other.profile_id}`}
                    className="ml-auto font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-1.5 border border-white/15 text-white/70 hover:text-pfc-gold hover:border-pfc-gold transition"
                  >
                    View Profile
                  </Link>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-3" data-testid="thread-messages">
                {thread.messages.length === 0 ? (
                  <div className="text-center text-white/40 font-sub uppercase tracking-[0.2em] text-xs py-12">
                    No messages yet. Say something.
                  </div>
                ) : thread.messages.map((m) => {
                  const mine = m.from_user_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                      <div className={`max-w-[78%] px-4 py-3 ${mine ? "bg-pfc-gold text-ink-900" : "bg-ink-700 text-white border border-white/10"}`} style={{ borderRadius: 10 }}>
                        {m.image_data_url && (
                          <img src={m.image_data_url} alt="" className="rounded mb-2 max-h-72 object-contain" />
                        )}
                        {m.body && <p className="font-body text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                        <p className={`mt-1 text-[10px] font-sub uppercase tracking-wider ${mine ? "text-ink-900/60" : "text-white/40"}`}>
                          {new Date(m.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="border-t border-white/10 p-4" data-testid="message-composer">
                {imageDataUrl && (
                  <div className="relative inline-block mb-3">
                    <img src={imageDataUrl} alt="" className="max-h-28 rounded border border-pfc-gold/40" />
                    <button type="button" onClick={() => setImageDataUrl(null)} className="absolute -top-2 -right-2 bg-pfc-red text-white w-6 h-6 rounded-full text-xs font-bold">×</button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write a message…"
                    rows={2} maxLength={2000}
                    className="flex-1 bg-ink-900 border border-white/15 text-white px-4 py-2 font-body focus:border-pfc-gold focus:outline-none resize-none"
                    data-testid="message-input"
                  />
                  <label className="cursor-pointer p-3 border border-white/15 text-white/70 hover:text-pfc-gold hover:border-pfc-gold transition" data-testid="message-image-btn">
                    <ImagePlus size={18} />
                    <input type="file" accept="image/*" hidden onChange={onPickImage} />
                  </label>
                  <button
                    type="submit" disabled={sending}
                    className="p-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
                    data-testid="message-send"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
